import { useState } from "react";
import { Check, ExternalLink, Gift, Pencil, Plus, ShieldCheck, ShieldAlert, Trash2, X, Zap } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import PerkFields, { perkSummary } from "@/components/admin/PerkFields";

const slug = (s) =>
  (s || "")
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9\s-]/g, "")
    .replace(/\s+/g, "-")
    .replace(/-+/g, "-")
    .replace(/^-+|-+$/g, "");

const BLANK = {
  product_name: "",
  product_url: "",
  prod_url: "",
  dev_url: "",
  product_key: "",
  signup_points: 0,
  conversion_points: 0,
  conversion_event_label: "",
  description: "",
  active: true,
  verify_payment_url: "",
  verify_payment_auth_header: "",
};

/**
 * ReferralProductsEditor
 * Props:
 *   value: ReferralProduct[]
 *   onChange: (next: ReferralProduct[]) => void
 *
 * Per-product row with edit/save/delete/toggle. product_key is auto-slugged and locked after creation.
 */
export default function ReferralProductsEditor({ value = [], onChange }) {
  const items = Array.isArray(value) ? value : [];
  const [editIdx, setEditIdx] = useState(-1);
  const [draft, setDraft] = useState(BLANK);
  const [adding, setAdding] = useState(false);
  const [newDraft, setNewDraft] = useState(BLANK);

  const update = (next) => onChange(next);

  const beginEdit = (i) => {
    setEditIdx(i);
    setDraft({ ...BLANK, ...items[i] });
  };
  const commitEdit = () => {
    if (!draft.product_name.trim()) return;
    const next = items.slice();
    const prodUrl = (draft.prod_url || draft.product_url || "").trim();
    next[editIdx] = {
      ...draft,
      product_name: draft.product_name.trim(),
      product_url: prodUrl, // keep legacy field synced with prod for back-compat
      prod_url: prodUrl,
      dev_url: (draft.dev_url || "").trim() || null,
      verify_payment_url: (draft.verify_payment_url || "").trim() || null,
      verify_payment_auth_header: (draft.verify_payment_auth_header || "").trim() || null,
      conversion_event_label: (draft.conversion_event_label || "").trim(),
      description: (draft.description || "").trim().slice(0, 150),
      signup_points: Number(draft.signup_points || 0),
      conversion_points: Number(draft.conversion_points || 0),
      active: !!draft.active,
      default_perk: draft.default_perk || null,
      // product_key is locked — keep existing
      product_key: items[editIdx].product_key,
    };
    update(next);
    setEditIdx(-1);
  };
  const cancelEdit = () => setEditIdx(-1);
  const remove = (i) => update(items.filter((_, idx) => idx !== i));
  const toggle = (i) => {
    const next = items.slice();
    next[i] = { ...next[i], active: !next[i].active };
    update(next);
  };
  const addNew = () => {
    const name = (newDraft.product_name || "").trim();
    if (!name) return;
    let key = slug(name);
    // ensure unique key
    let i = 2;
    while (items.some((p) => p.product_key === key)) {
      key = `${slug(name)}-${i++}`;
    }
    const prodUrl = (newDraft.prod_url || newDraft.product_url || "").trim();
    update([
      ...items,
      {
        ...newDraft,
        product_name: name,
        product_key: key,
        product_url: prodUrl,
        prod_url: prodUrl,
        dev_url: (newDraft.dev_url || "").trim() || null,
        verify_payment_url: (newDraft.verify_payment_url || "").trim() || null,
        verify_payment_auth_header: (newDraft.verify_payment_auth_header || "").trim() || null,
        conversion_event_label: (newDraft.conversion_event_label || "").trim(),
        description: (newDraft.description || "").trim().slice(0, 150),
        signup_points: Number(newDraft.signup_points || 0),
        conversion_points: Number(newDraft.conversion_points || 0),
        active: true,
      },
    ]);
    setNewDraft(BLANK);
    setAdding(false);
  };

  const fieldCls =
    "w-full rounded border border-zinc-700 bg-zinc-950 px-2 py-1.5 text-sm text-white outline-none focus:border-[#A855F7]";

  return (
    <div className="space-y-3" data-testid="referral-products-editor">
      <div className="overflow-x-auto rounded-lg border border-zinc-800">
        <table className="w-full text-sm">
          <thead className="bg-zinc-900/60 text-[10px] font-bold uppercase tracking-wide text-zinc-500">
            <tr>
              <th className="px-3 py-2 text-left">Product</th>
              <th className="px-3 py-2 text-left">Prod URL</th>
              <th className="px-3 py-2 text-left">Dev URL</th>
              <th className="px-3 py-2 text-right">Signup pts</th>
              <th className="px-3 py-2 text-right">Conv. pts</th>
              <th className="px-3 py-2 text-left">Conv. event label</th>
              <th className="px-3 py-2 text-left">Description</th>
              <th className="px-3 py-2 text-center">Active</th>
              <th className="px-3 py-2"></th>
            </tr>
          </thead>
          <tbody className="divide-y divide-zinc-900">
            {items.map((p, i) =>
              editIdx === i ? (
                <tr key={p.product_key} data-testid={`rp-edit-row-${p.product_key}`} className="bg-zinc-900/40">
                  <td className="px-3 py-2">
                    <input className={fieldCls} value={draft.product_name} onChange={(e) => setDraft({ ...draft, product_name: e.target.value })} data-testid="rp-edit-name" />
                    <p className="mt-1 font-mono text-[10px] text-zinc-500">key: {p.product_key} <span className="text-zinc-700">(locked)</span></p>
                  </td>
                  <td className="px-3 py-2">
                    <input className={fieldCls} value={draft.prod_url || ""} onChange={(e) => setDraft({ ...draft, prod_url: e.target.value })} data-testid="rp-edit-prod-url" placeholder="https://your-product.com" />
                  </td>
                  <td className="px-3 py-2">
                    <input className={fieldCls} value={draft.dev_url || ""} onChange={(e) => setDraft({ ...draft, dev_url: e.target.value })} data-testid="rp-edit-dev-url" placeholder="https://staging.your-product.com (optional)" />
                  </td>
                  <td className="px-3 py-2"><input type="number" min={0} className={`${fieldCls} text-right`} value={draft.signup_points} onChange={(e) => setDraft({ ...draft, signup_points: e.target.value })} data-testid="rp-edit-signup" /></td>
                  <td className="px-3 py-2"><input type="number" min={0} className={`${fieldCls} text-right`} value={draft.conversion_points} onChange={(e) => setDraft({ ...draft, conversion_points: e.target.value })} data-testid="rp-edit-conv" /></td>
                  <td className="px-3 py-2"><input className={fieldCls} value={draft.conversion_event_label} onChange={(e) => setDraft({ ...draft, conversion_event_label: e.target.value })} data-testid="rp-edit-event" /></td>
                  <td className="px-3 py-2"><input className={fieldCls} maxLength={150} value={draft.description} onChange={(e) => setDraft({ ...draft, description: e.target.value })} data-testid="rp-edit-desc" placeholder="Shown to ambassadors" /></td>
                  <td className="px-3 py-2 text-center">
                    <input type="checkbox" className="h-4 w-4 accent-[#A855F7]" checked={!!draft.active} onChange={(e) => setDraft({ ...draft, active: e.target.checked })} />
                  </td>
                  <td className="px-3 py-2 text-right">
                    <button onClick={commitEdit} className="rounded p-1 text-[#A855F7] hover:bg-[#A855F7]/10" title="Save" data-testid={`rp-save-${p.product_key}`}><Check className="h-4 w-4" /></button>
                    <button onClick={cancelEdit} className="rounded p-1 text-zinc-500 hover:bg-zinc-800" title="Cancel"><X className="h-4 w-4" /></button>
                  </td>
                </tr>
              ) : (
                <tr key={p.product_key} data-testid={`rp-row-${p.product_key}`}>
                  <td className="px-3 py-2">
                    <div className="font-semibold text-white">{p.product_name}</div>
                    <div className="font-mono text-[10px] text-zinc-500">{p.product_key}</div>
                    {p.default_perk?.active && (
                      <span data-testid={`rp-perk-${p.product_key}`} className="mt-1 mr-1 inline-flex items-center gap-1 rounded border border-[#A855F7]/40 bg-[#A855F7]/10 px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wider text-[#A855F7]" title="Default referral perk">
                        <Gift className="h-2.5 w-2.5" /> {perkSummary(p.default_perk)}
                      </span>
                    )}
                    {p.verify_payment_url ? (
                      <span data-testid={`rp-verify-${p.product_key}`} className="mt-1 inline-flex items-center gap-1 rounded border border-emerald-500/30 bg-emerald-500/10 px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wider text-emerald-300" title={`Payment verified via ${p.verify_payment_url}`}>
                        <ShieldCheck className="h-2.5 w-2.5" /> Verified
                      </span>
                    ) : (
                      <span className="mt-1 inline-flex items-center gap-1 rounded border border-amber-500/30 bg-amber-500/10 px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wider text-amber-300" title="No payment verification — converts are trusted blindly. Add a verify URL for safety.">
                        <ShieldAlert className="h-2.5 w-2.5" /> Unverified
                      </span>
                    )}
                  </td>
                  <td className="px-3 py-2">
                    {(p.prod_url || p.product_url) ? (
                      <a href={p.prod_url || p.product_url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 break-all text-xs text-[#A855F7] hover:underline">
                        {p.prod_url || p.product_url} <ExternalLink className="h-3 w-3 shrink-0" />
                      </a>
                    ) : <span className="text-xs text-zinc-600">—</span>}
                  </td>
                  <td className="px-3 py-2">
                    {p.dev_url ? (
                      <a href={p.dev_url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 break-all text-xs text-amber-300 hover:underline">
                        {p.dev_url} <ExternalLink className="h-3 w-3 shrink-0" />
                      </a>
                    ) : <span className="text-xs text-zinc-600">—</span>}
                  </td>
                  <td className="px-3 py-2 text-right font-mono text-[#A855F7]">{p.signup_points}</td>
                  <td className="px-3 py-2 text-right font-mono text-[#A855F7]">{p.conversion_points}</td>
                  <td className="px-3 py-2 text-xs text-zinc-300">{p.conversion_event_label || "—"}</td>
                  <td className="px-3 py-2 text-xs text-zinc-400">{p.description || <span className="text-zinc-600">—</span>}</td>
                  <td className="px-3 py-2 text-center">
                    <button
                      type="button"
                      onClick={() => toggle(i)}
                      className={`relative inline-flex h-5 w-9 items-center rounded-full transition ${p.active ? "bg-[#A855F7]" : "bg-zinc-700"}`}
                      data-testid={`rp-toggle-${p.product_key}`}
                      aria-label={p.active ? "Active" : "Inactive"}
                    >
                      <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition ${p.active ? "translate-x-4" : "translate-x-0.5"}`} />
                    </button>
                  </td>
                  <td className="px-3 py-2 text-right">
                    <button onClick={() => beginEdit(i)} className="rounded p-1 text-zinc-300 hover:bg-zinc-800" title="Edit" data-testid={`rp-edit-${p.product_key}`}><Pencil className="h-4 w-4" /></button>
                    <button onClick={() => remove(i)} className="rounded p-1 text-rose-400 hover:bg-rose-500/10" title="Delete" data-testid={`rp-delete-${p.product_key}`}><Trash2 className="h-4 w-4" /></button>
                  </td>
                </tr>
              )
            )}
            {items.length === 0 && (
              <tr><td colSpan={9} className="px-3 py-6 text-center text-xs text-zinc-500">No referral products yet — add the first one below.</td></tr>
            )}
          </tbody>
        </table>
      </div>

      {editIdx >= 0 && (
        <VerifyPaymentPanel
          draft={draft}
          setDraft={setDraft}
          productName={items[editIdx]?.product_name}
        />
      )}

      {editIdx >= 0 && (
        <div className="rounded-lg border border-[#A855F7]/30 bg-[#A855F7]/[0.04] p-4" data-testid="rp-perk-panel">
          <p className="mb-3 flex items-center gap-1.5 text-xs font-bold uppercase tracking-wide text-[#A855F7]">
            <Gift className="h-3.5 w-3.5" /> Default referral perk — {items[editIdx]?.product_name}
          </p>
          <p className="mb-3 text-[11px] text-zinc-500">
            The discount recruits get when they use ANY ambassador&apos;s code for this product (unless overridden per-ambassador). Product sites read it via the perk API.
          </p>
          <PerkFields perk={draft.default_perk || null} onChange={(p) => setDraft({ ...draft, default_perk: p })} idPrefix="rp-perk" />
        </div>
      )}

      {adding ? (
        <div className="rounded-lg border border-[#A855F7]/40 bg-zinc-900/40 p-4">
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
            <div>
              <label className="mb-1 block text-[10px] font-bold uppercase tracking-wide text-zinc-500">Product name *</label>
              <input className={fieldCls} value={newDraft.product_name} onChange={(e) => setNewDraft({ ...newDraft, product_name: e.target.value })} autoFocus data-testid="rp-new-name" />
              {newDraft.product_name && <p className="mt-1 font-mono text-[10px] text-zinc-500">key: {slug(newDraft.product_name)}</p>}
            </div>
            <div>
              <label className="mb-1 block text-[10px] font-bold uppercase tracking-wide text-zinc-500">Prod URL *</label>
              <input className={fieldCls} value={newDraft.prod_url} onChange={(e) => setNewDraft({ ...newDraft, prod_url: e.target.value })} placeholder="https://your-product.com" data-testid="rp-new-prod-url" />
            </div>
            <div>
              <label className="mb-1 block text-[10px] font-bold uppercase tracking-wide text-zinc-500">Dev URL <span className="font-normal normal-case text-zinc-600">(optional)</span></label>
              <input className={fieldCls} value={newDraft.dev_url} onChange={(e) => setNewDraft({ ...newDraft, dev_url: e.target.value })} placeholder="https://staging.your-product.com" data-testid="rp-new-dev-url" />
            </div>
            <div>
              <label className="mb-1 block text-[10px] font-bold uppercase tracking-wide text-zinc-500">Signup points</label>
              <input type="number" min={0} className={fieldCls} value={newDraft.signup_points} onChange={(e) => setNewDraft({ ...newDraft, signup_points: e.target.value })} data-testid="rp-new-signup" />
            </div>
            <div>
              <label className="mb-1 block text-[10px] font-bold uppercase tracking-wide text-zinc-500">Conversion points</label>
              <input type="number" min={0} className={fieldCls} value={newDraft.conversion_points} onChange={(e) => setNewDraft({ ...newDraft, conversion_points: e.target.value })} data-testid="rp-new-conv" />
            </div>
            <div className="lg:col-span-2">
              <label className="mb-1 block text-[10px] font-bold uppercase tracking-wide text-zinc-500">Conversion event label</label>
              <input className={fieldCls} value={newDraft.conversion_event_label} onChange={(e) => setNewDraft({ ...newDraft, conversion_event_label: e.target.value })} placeholder='e.g. "Payment success"' data-testid="rp-new-event" />
            </div>
            <div className="lg:col-span-4">
              <label className="mb-1 block text-[10px] font-bold uppercase tracking-wide text-zinc-500">Description (max 150 chars, shown to ambassadors)</label>
              <input className={fieldCls} maxLength={150} value={newDraft.description} onChange={(e) => setNewDraft({ ...newDraft, description: e.target.value })} data-testid="rp-new-desc" />
            </div>
          </div>
          <VerifyPaymentPanel draft={newDraft} setDraft={setNewDraft} productName={newDraft.product_name || "this product"} />
          <div className="mt-3 flex justify-end gap-2">
            <button onClick={() => { setAdding(false); setNewDraft(BLANK); }} className="rounded-md border border-zinc-700 px-3 py-1.5 text-sm text-zinc-300 hover:bg-zinc-900" data-testid="rp-cancel-add">Cancel</button>
            <button onClick={addNew} className="inline-flex items-center gap-1.5 rounded-md bg-[#A855F7] px-4 py-1.5 text-sm font-bold text-white" data-testid="rp-confirm-add"><Check className="h-4 w-4" /> Add</button>
          </div>
        </div>
      ) : (
        <button onClick={() => setAdding(true)} data-testid="rp-add-btn" className="inline-flex items-center gap-1.5 rounded-md border border-zinc-800 bg-zinc-900 px-3 py-1.5 text-xs font-semibold text-zinc-200 hover:border-[#A855F7]/60 hover:text-white">
          <Plus className="h-3.5 w-3.5" /> Add product
        </button>
      )}

      <p className="text-xs text-zinc-500">
        Each row generates an immutable <code className="rounded bg-zinc-900 px-1 text-[#A855F7]">product_key</code> used by webhook calls (
        <code className="rounded bg-zinc-900 px-1">POST /api/referrals/track</code> and{" "}
        <code className="rounded bg-zinc-900 px-1">/convert</code>). The <strong className="text-zinc-300">Integrations tab&apos;s Environment</strong> toggle (prod / dev) decides which URL ambassadors get for sharing.
      </p>
    </div>
  );
}

function VerifyPaymentPanel({ draft, setDraft, productName }) {
  const [testing, setTesting] = useState(false);
  const [result, setResult] = useState(null);
  const [testRef, setTestRef] = useState("");

  const fieldCls = "w-full rounded border border-zinc-700 bg-zinc-950 px-2 py-1.5 text-sm text-white outline-none focus:border-[#A855F7]";

  const runTest = async () => {
    if (!draft.verify_payment_url) {
      toast.error("Add a verify URL first");
      return;
    }
    setTesting(true);
    setResult(null);
    try {
      const r = await api.post("/admin/webhook-events/test-verify", {
        url: draft.verify_payment_url,
        auth_header: draft.verify_payment_auth_header || "",
        payment_reference: testRef || "TEST-REF-PING",
      });
      setResult(r.data);
      if (r.data.would_succeed) {
        toast.success("Endpoint reachable and confirms payment");
      } else if (r.data.ok) {
        toast.error("Endpoint reachable but response is NOT a success — fix the response format");
      } else {
        toast.error(r.data.error || "Could not reach the endpoint");
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Test failed");
    } finally {
      setTesting(false);
    }
  };

  return (
    <div className="mt-3 rounded-lg border border-amber-500/30 bg-amber-500/[0.04] p-4" data-testid="verify-payment-panel">
      <div className="flex items-baseline justify-between gap-3">
        <div>
          <h4 className="font-display text-sm font-bold text-amber-200">
            <ShieldCheck className="mr-1 inline h-3.5 w-3.5" /> Payment verification for {productName || "product"}
          </h4>
          <p className="mt-0.5 text-[11px] text-zinc-400">
            When set, our <code className="text-[#A855F7]">/convert</code> webhook will <strong className="text-zinc-200">call this URL</strong> with the <code className="text-[#A855F7]">payment_reference</code> and only award points if it confirms <code>{`{status:"succeeded"}`}</code> or <code>{`{paid:true}`}</code>. Leave blank to skip (less safe).
          </p>
        </div>
      </div>
      <div className="mt-3 grid gap-3 sm:grid-cols-2">
        <div>
          <label className="mb-1 block text-[10px] font-bold uppercase tracking-wide text-zinc-500">Verify URL <span className="font-normal normal-case text-zinc-600">(use {`{payment_reference}`} placeholder)</span></label>
          <input
            className={fieldCls}
            value={draft.verify_payment_url || ""}
            onChange={(e) => setDraft({ ...draft, verify_payment_url: e.target.value })}
            placeholder="https://innovate.noobsync.com/api/payments/{payment_reference}/status"
            data-testid="rp-verify-url"
          />
        </div>
        <div>
          <label className="mb-1 block text-[10px] font-bold uppercase tracking-wide text-zinc-500">Auth header <span className="font-normal normal-case text-zinc-600">(format: <code>Header-Name: value</code>)</span></label>
          <input
            className={fieldCls}
            value={draft.verify_payment_auth_header || ""}
            onChange={(e) => setDraft({ ...draft, verify_payment_auth_header: e.target.value })}
            placeholder="X-API-Key: your-ideathon-key"
            data-testid="rp-verify-auth"
          />
        </div>
      </div>
      <div className="mt-3 flex flex-wrap items-center gap-2">
        <input
          className={`${fieldCls} max-w-xs`}
          value={testRef}
          onChange={(e) => setTestRef(e.target.value)}
          placeholder="Test payment_reference (e.g. pi_xxx)"
          data-testid="rp-verify-test-ref"
        />
        <button
          type="button"
          onClick={runTest}
          disabled={testing || !draft.verify_payment_url}
          className="inline-flex items-center gap-1.5 rounded-md bg-amber-500 px-3 py-1.5 text-xs font-bold text-black hover:bg-amber-400 disabled:opacity-50"
          data-testid="rp-verify-test"
        >
          <Zap className="h-3 w-3" /> {testing ? "Pinging…" : "Test verify URL"}
        </button>
        {result && (
          <span className={`inline-flex items-center gap-1 rounded border px-2 py-1 text-[10px] font-bold uppercase tracking-wider ${result.would_succeed ? "border-emerald-500/40 bg-emerald-500/10 text-emerald-300" : result.ok ? "border-amber-500/40 bg-amber-500/10 text-amber-300" : "border-rose-500/40 bg-rose-500/10 text-rose-300"}`}>
            {result.would_succeed ? "✓ Would award points" : result.ok ? `⚠ HTTP ${result.status_code} — not a success response` : `✗ ${result.error?.slice(0, 60) || "unreachable"}`}
          </span>
        )}
      </div>
      {result?.response && (
        <pre className="mt-3 max-h-32 overflow-auto rounded border border-zinc-800 bg-black p-2 font-mono text-[10px] text-zinc-300" data-testid="rp-verify-result">
{JSON.stringify(result.response, null, 2)}
        </pre>
      )}
    </div>
  );
}
